import { useEffect, type ReactNode } from "react";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from "react-router";
import { onUnauthorized } from "./lib/api";
import { ME_KEY, useMe } from "./lib/auth";
import { syncDisplayTimeZone, useSettings } from "./lib/queries";
import { EcgLoader } from "./components/Ecg";
import { Shell } from "./components/Shell";
import { ConfirmProvider, ToastProvider } from "./components/ui";
import { LoginPage } from "./pages/Login";
import { routes } from "./routes";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: true, staleTime: 5_000, retry: 1 },
    mutations: { retry: 0 },
  },
});
// время везде — в поясе из настроек платформы
syncDisplayTimeZone(queryClient);

function Providers() {
  const qc = useQueryClient();
  useEffect(() => onUnauthorized(() => void qc.invalidateQueries({ queryKey: ME_KEY })), [qc]);
  return (
    <ToastProvider>
      <ConfirmProvider>
        <Outlet />
      </ConfirmProvider>
    </ToastProvider>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isLoading, error } = useMe();
  // страницы рисуем уже в поясе из настроек; не загрузились — остаётся пояс браузера
  const settings = useSettings(!!me?.user);
  const loc = useLocation();
  if (isLoading || (me?.user && settings.isPending)) {
    return (
      <div className="page-loading">
        <EcgLoader label="Открываю студию" />
      </div>
    );
  }
  if (error && !me) {
    return (
      <div className="page-loading">
        <p>Платформа не отвечает. Проверьте, что она запущена, и обновите страницу.</p>
      </div>
    );
  }
  if (!me?.user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    element: <Providers />,
    children: [
      { path: "/login", element: <LoginPage /> },
      {
        path: "/",
        element: (
          <RequireAuth>
            <Shell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Navigate to="/write" replace /> },
          ...routes,
          { path: "*", element: <Navigate to="/write" replace /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
